// DateRangeToggle — reusable Day/Week/Month/Quarter/Year/All Time pill toggle.
// Pair with the useDateRange hook (or inline sinceDate logic) to filter data client-side.

import { useMemo } from "react";

export type DateRange = "day" | "week" | "month" | "quarter" | "year" | "all";

interface Option {
  key: DateRange;
  label: string;
}

const OPTIONS: Option[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
  { key: "all", label: "All" },
];

interface DateRangeToggleProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export default function DateRangeToggle({
  value,
  onChange,
}: DateRangeToggleProps) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {OPTIONS.map(({ key, label }) => {
        const isActive = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`
              px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px]
              ${
                isActive
                  ? "bg-[#0E403A] text-white shadow-sm"
                  : "bg-theme-overlay text-theme-muted hover:text-theme-primary hover:bg-theme-overlay/80"
              }
            `}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Given a DateRange, compute the ISO string of the earliest date to include.
 * Returns null for 'all' (no filtering).
 */
export function computeSinceDate(range: DateRange): string | null {
  const now = new Date();
  switch (range) {
    case "day":
      return new Date(now.getTime() - 86_400_000).toISOString();
    case "week":
      return new Date(now.getTime() - 7 * 86_400_000).toISOString();
    case "month": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      return d.toISOString();
    }
    case "quarter": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      return d.toISOString();
    }
    case "year":
      return `${now.getFullYear()}-01-01T00:00:00.000Z`;
    case "all":
      return null;
  }
}

/**
 * Hook that memoises sinceDate for a given DateRange.
 * Returns null for 'all'.
 */
export function useSinceDate(range: DateRange): string | null {
  return useMemo(() => computeSinceDate(range), [range]);
}

/** Returns true if a date string is on or after sinceDate (or sinceDate is null). */
export function isAfterSince(
  dateStr: string | null | undefined,
  sinceDate: string | null,
): boolean {
  if (sinceDate === null) return true;
  if (!dateStr) return false;
  return dateStr >= sinceDate;
}
